// The "What's new" note after an update: a small chip on the start page (a bubble in Island Mode)
// that opens /whats-new at the newest release. The release is written into the page at build time
// from CHANGELOG.md (<meta name="rv-release">), so nothing is fetched. A brand new browser never
// sees it: the first visit only remembers which release it started on. It shows once per release
// until it's opened or closed, and goes away by itself after two weeks. The chip is small, so it is
// drawn here in the same frame as the start page instead of from a lazy chunk: a chip that arrived
// later would push the search box and shortcuts down.
import { NEWS_KEY, decideNews, anchorOf, newsLabel } from "../shared/notes-core.js";
import { metric } from "../shared/metric.js";
import { openInternal } from "./tabs.js";

const X_SVG = '<svg viewBox="0 0 12 12" aria-hidden="true" focusable="false"><path d="M3 3l6 6M9 3l-6 6"/></svg>';
function btn(cls, onClick) { const b = document.createElement("button"); b.className = cls; b.type = "button"; b.addEventListener("click", onClick); return b; }
const read = () => { try { return JSON.parse(localStorage.getItem(NEWS_KEY)) || null; } catch { return null; } };
const write = (o) => { try { localStorage.setItem(NEWS_KEY, JSON.stringify(o)); } catch { /* storage off: it just shows again */ } };
function release() {
	const m = document.querySelector('meta[name="rv-release"]');
	const v = m ? m.getAttribute("content") || "" : "";
	return /^\d+\.\d+\.\d+$/.test(v) ? { v, name: m.dataset.name || "" } : null;
}
// Has this browser used Re:Vault on an earlier day (from the study ledger)?
function usedBefore(today) {
	try { return Object.keys(JSON.parse(localStorage.getItem("revault:days") || "{}") || {}).some((k) => /^\d{4}-\d{2}-\d{2}$/.test(k) && k < today); } catch { return false; }
}

// Seen: closed, or opened. The chip (if it is up) hears about it and goes.
export function markNewsDone(how) {
	const rel = release();
	if (!rel) return;
	const s = read();
	write({ v: rel.v, at: s && s.v === rel.v ? s.at : Date.now(), done: 1 });
	dispatchEvent(new CustomEvent("rv-news-done", { detail: how }));
}
export function openWhatsNew() {
	const rel = release();
	openInternal("/whats-new" + (rel ? "#" + anchorOf(rel.v) : ""), "What's new");
	markNewsDone("open");
}

export function initWhatsNew(ctx) {
	const rel = release();
	if (!rel || ctx.inOS) return;
	const now = Date.now(), d = new Date(now), seen = read();
	const today = d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
	const what = decideNews(rel, seen, now, usedBefore(today));
	if (what === "remember") { write({ v: rel.v, at: now, done: 1 }); return; }
	if (what !== "show") return;
	if (!seen || seen.v !== rel.v) write({ v: rel.v, at: now });
	newsNote(ctx, { rel, read, open: openWhatsNew, done: markNewsDone });
}

// ctx: { get, subscribe, emitIsland, islandOn }. n: { rel, read, open, done(how) }
function newsNote(ctx, n) {
	const label = newsLabel(n.rel);
	const box = document.getElementById("rv-news");
	const on = () => ctx.get("whatsNew") !== false && !(n.read() || {}).done;
	function close() {
		n.done("dismiss");
		const to = document.getElementById("rv-startsearch-input");
		if (to && to.offsetParent) to.focus();
	}
	function paint() {
		const yes = on();
		ctx.emitIsland("news", yes ? { text: label, open: n.open, dismiss: () => n.done("dismiss") } : null);
		if (!box) return;
		if (yes && !box.firstChild) {
			const go = btn("rv-news-go", n.open);
			go.innerHTML = '<span class="rv-news-tag" aria-hidden="true">New</span>';
			go.append(document.createTextNode(label));
			const x = btn("rv-news-x", close);
			x.setAttribute("aria-label", "Hide the What's new note");
			x.title = "Hide";
			x.innerHTML = X_SVG;
			box.append(go, x);
		}
		box.hidden = !yes || ctx.islandOn();
	}
	addEventListener("rv-news-done", (e) => { metric("whatsnew:" + (e.detail === "open" ? "open" : "dismiss")); paint(); });
	paint();
	ctx.subscribe(paint);
}
